import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";

export default function Register() {
  const nav = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ agency_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Agency created!");
      nav("/agency");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="register-form">
          <Link to="/" className="inline-flex items-center gap-2 mb-10" data-testid="brand-link">
            <img src="/logo-mark.png" alt="Foliosy" className="h-9 w-9 object-contain" />
            <span className="font-bold">Foliosy</span>
          </Link>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900 mb-2">Create your agency</h1>
          <p className="text-slate-600 mb-8">Everything your clients need — your brand, your domain.</p>

          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Agency name</label>
          <input
            required
            value={form.agency_name}
            onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
            data-testid="register-agency-input"
          />

          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Work email</label>
          <input
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-4 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
            data-testid="register-email-input"
          />

          <label className="text-sm font-medium text-slate-700 mb-1.5 block">Password</label>
          <input
            type="password"
            minLength={6}
            required
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 mb-6 focus:bg-white focus:ring-2 focus:ring-slate-900/5 focus:border-slate-900 outline-none"
            data-testid="register-password-input"
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-slate-900 text-white rounded-full py-3 font-medium hover:bg-slate-700 active:scale-95 disabled:opacity-50"
            data-testid="register-submit-button"
          >
            {loading ? "Creating…" : "Create agency"}
          </button>

          <p className="mt-6 text-sm text-slate-600">
            Already have an account?{" "}
            <Link to="/login" className="text-slate-900 font-medium underline" data-testid="go-login-link">
              Sign in
            </Link>
          </p>
        </form>
      </div>

      <div className="hidden md:block md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/6476580/pexels-photo-6476580.jpeg"
          alt="Workspace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 to-slate-900/20" />
        <div className="relative z-10 p-12 h-full flex items-end">
          <div className="glass rounded-3xl p-6 text-slate-900 max-w-xs">
            <p className="eyebrow mb-2">Pillar 1</p>
            <h3 className="text-2xl font-bold mb-2">White-label portals in minutes.</h3>
            <p className="text-sm text-slate-600">Your clients see only you.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
